import React, { useCallback, useEffect, useMemo, useState } from "react";
import CreativeSelectionFormBase from "./CreativeSelectionForm";
import { useFormikContext } from "formik";
import RpVirtualListBase from "@/components/virtual/RpVirtualList";
import ExtendCreativeBulkSelectionBase from "./ExtendCreativeBulkSelection";
import { Button, Checkbox, Grid as GridBase } from "@mui/material";
import { Box, Paper } from "@mui/material";
import MuiSearchInputBase from "@/components/muiCustom/search/MuiSearchInput";
import useSearchFilter from "@/helpers/hooks/useSearchFilter";
import ExpendTotalBudgetBase from "./ExpendTotalBudget";
import RpAlertBase from "@/components/alerts/RpAlert";

type LocationId = number | string;

type CreativeLocation = {
  location_id: LocationId;
  location_name?: string;
  [key: string]: unknown;
};

type SelectedLocationValue = {
  extend_start_date?: unknown;
  extend_end_date?: unknown;
  extend_budget?: unknown;
  can_update_creatives?: boolean;
  [key: string]: unknown;
};

type CreativeSelectionValues = {
  selectedLoc?: Record<LocationId, SelectedLocationValue>;
  [key: string]: unknown;
};

type CreativeSelectionProps = {
  locationData: CreativeLocation[];
  campaignData?: Record<string, unknown>;
};

const CreativeSelectionForm =
  CreativeSelectionFormBase as unknown as React.ComponentType<
    Record<string, unknown>
  >;

const RpVirtualList = RpVirtualListBase as unknown as React.ComponentType<
  Record<string, unknown>
>;

const ExtendCreativeBulkSelection =
  ExtendCreativeBulkSelectionBase as unknown as React.ComponentType<
    Record<string, unknown>
  >;

const Grid = GridBase as React.ComponentType<
  Record<string, unknown> & { children?: React.ReactNode }
>;

const MuiSearchInput = MuiSearchInputBase as React.ComponentType<
  Record<string, unknown>
>;

const ExpendTotalBudget = ExpendTotalBudgetBase as React.ComponentType<
  Record<string, unknown>
>;

const RpAlert = RpAlertBase as React.ComponentType<
  Record<string, unknown> & { children?: React.ReactNode }
>;

function CreativeSelection({
  locationData,
  campaignData,
}: CreativeSelectionProps) {
  const { values, setValues } = useFormikContext<CreativeSelectionValues>();

  const [expanded, setExpanded] = useState<LocationId[]>([]);

  const [showLocationCheckBox, setShowLocationCheckBox] = useState(false);
  const [checkedLocations, setCheckedLocations] = useState<LocationId[]>([]);
  const [disableLocations, setDisableLocations] = useState<LocationId[]>([]);

  const filledLocs = useMemo(() => {
    // only show locations that have extend start date, end date and budget filled for creative update
    return locationData.filter((loc) => {
      const locationId = loc.location_id;
      const lc = values?.selectedLoc?.[locationId];
      return lc?.extend_start_date && lc?.extend_end_date && lc?.extend_budget;
    });
  }, [locationData, values?.selectedLoc]);

  const [searchKey, handleSearchChange, filteredSubscribedLocations] =
    useSearchFilter(filledLocs, ["location_name"]) as [
      string,
      (..._args: unknown[]) => void,
      CreativeLocation[],
    ];

  useEffect(() => {
    if (filledLocs?.length < 5) {
      setExpanded(filledLocs.map((loc) => loc.location_id));
    } else {
      setExpanded([]);
    }
  }, [filledLocs]);

  useEffect(() => {
    if (filledLocs?.length) {
      setDisableLocations(
        filledLocs
          .filter(
            (loc) =>
              !values?.selectedLoc?.[loc.location_id]?.can_update_creatives
          )
          ?.map((loc) => loc.location_id)
      );
    }
  }, [filledLocs, values?.selectedLoc]);

  const handleToggleOpenBulkSelection = useCallback((isOpen = true) => {
    setShowLocationCheckBox(isOpen);
    if (!isOpen) {
      setCheckedLocations([]);
    } else {
      setExpanded([]);
    }
  }, []);

  return (
    <div className="position-relative pb-5">
      {filledLocs?.length > 1 && (
        <ExtendCreativeBulkSelection
          onToggleBulkSelection={handleToggleOpenBulkSelection}
          campaignData={campaignData}
          checkedLocations={checkedLocations}
          setLocationValues={setValues}
        />
      )}

      {showLocationCheckBox && (
        <Grid container spacing={1} alignItems="center">
          <Grid item>
            <Paper>
              <Checkbox
                color="primary"
                checked={
                  disableLocations?.length > 0 &&
                  disableLocations?.length < filledLocs?.length
                    ? checkedLocations?.length ===
                      filledLocs?.length - disableLocations?.length
                    : checkedLocations?.length === filledLocs?.length
                }
                onChange={(e) => {
                  if (e.target.checked) {
                    setCheckedLocations(
                      (_prev) =>
                        filledLocs
                          .map((loc) => loc.location_id)
                          .filter((id) => !disableLocations.includes(id)) // don't select locations that are disabled
                    );
                  } else {
                    setCheckedLocations([]);
                  }
                }}
                className="m-0 p-1"
              />
            </Paper>
          </Grid>
          <Grid item size={{ xs: 10, md: 6, lg: 4 }}>
            <MuiSearchInput
              placeholder="Search.."
              value={searchKey}
              onChange={handleSearchChange}
            />
          </Grid>
        </Grid>
      )}
      {filteredSubscribedLocations &&
        filteredSubscribedLocations?.length > 0 && (
          <div className="d-flex justify-content-end mb-2">
            <Button
              variant="outlined"
              color="primary"
              onClick={() =>
                setExpanded(
                  expanded.length === filledLocs.length
                    ? []
                    : filledLocs.map((loc) => loc.location_id)
                )
              }
            >
              {expanded.length === filledLocs.length
                ? "Collapse all"
                : "Expand all"}{" "}
            </Button>
          </div>
        )}
      {searchKey &&
      filteredSubscribedLocations &&
      filteredSubscribedLocations?.length === 0 ? (
        <div className="my-5 text-center">
          <RpAlert severity="info">
            <strong>No locations found.</strong>
          </RpAlert>
        </div>
      ) : (
        <RpVirtualList
          items={filteredSubscribedLocations}
          renderItem={(loc: CreativeLocation, index: number) => {
            const locationId = loc.location_id;
            const lc = values?.selectedLoc?.[locationId];

            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "start",
                }}
                key={locationId}
              >
                {showLocationCheckBox && (
                  <Paper className="mr-1 mt-2 d-inline-flex align-items-center">
                    <Checkbox
                      color="primary"
                      checked={checkedLocations.includes(loc.location_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setCheckedLocations((prev) => [
                            ...prev,
                            loc.location_id,
                          ]);
                        } else {
                          setCheckedLocations((prev) =>
                            prev.filter((id) => id !== loc.location_id)
                          );
                        }
                      }}
                      className="m-0 p-1"
                      disabled={disableLocations.includes(loc.location_id)}
                    />
                  </Paper>
                )}
                <div className="w-100">
                  <CreativeSelectionForm
                    key={locationId}
                    location_id={locationId}
                    lc={lc}
                    loc={loc}
                    index={index}
                    campaignData={campaignData}
                    expanded={expanded}
                    setExpanded={setExpanded}
                  />
                </div>
              </Box>
            );
          }}
        />
      )}

      <ExpendTotalBudget values={values} />
    </div>
  );
}

export default CreativeSelection;
